import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore modulo JS legacy senza tipi dedicati
import { personnelApi } from '../../api/personnelApi';
// @ts-ignore
import EmployeeCreateModal from '../../components/personnel/EmployeeCreateModal';
// @ts-ignore
import EmployeeEditModal from '../../components/personnel/EmployeeEditModal';
// @ts-ignore
import EmployeeDetails from '../../components/personnel/EmployeeDetails';

type ContractInfo = {
  id: string;
  type: string;
  startDate: string;
  endDate?: string | null;
};

type LeaveInfo = {
  accruedDays: number;
  usedDays: number;
  permessiDays?: number;
};

type Employee = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  matricola?: string | null;
  fiscalCode?: string | null;
  address?: string | null;
  status?: string | null;
  // Anagrafica estesa
  birthDate?: string | null;
  birthPlace?: string | null;
  gender?: string | null;
  levelCode?: string | null;
  qualification?: string | null;
  contracts?: ContractInfo[];
  leave?: LeaveInfo | null;
};

export default function PersonnelPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [showDetails, setShowDetails] = useState<Employee | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: string[] } | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await personnelApi.listEmployees();
      setEmployees(res ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del personale');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFileName(file.name);
    setImportResult(null);
    setImporting(true);
    try {
      const text = await file.text();
      const res = await personnelApi.importEmployees(text);
      setImportResult(res);
      await load();
    } catch (err: unknown) {
      setImportResult({ imported: 0, errors: [err instanceof Error ? err.message : 'Errore import'] });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleCancelFile = () => {
    setUploadedFileName(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (e: Employee) => {
    if (!window.confirm(`Eliminare ${e.lastName ?? ''} ${e.firstName ?? ''}? Operazione irreversibile.`)) return;
    try {
      await personnelApi.deleteEmployee(e.id);
      await load();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        `Eliminare l'elenco completo di ${employees.length} dipendenti? Verranno cancellati anche contratti, ferie, paghe e presenze. Operazione irreversibile.`
      )
    )
      return;
    setDeletingAll(true);
    try {
      const res = await personnelApi.deleteAllEmployees();
      const count = (res as { deleted?: number })?.deleted ?? 0;
      setEmployees([]);
      await load();
      alert(`Eliminati ${count} dipendenti.`);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Errore eliminazione elenco');
    } finally {
      setDeletingAll(false);
    }
  };

  const contractsExpiring = employees.filter((e) => {
    const end = e.contracts?.[0]?.endDate;
    if (!end) return false;
    const d = new Date(end);
    const in30d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return d <= in30d && d >= new Date();
  }).length;

  const totalResidualLeave = employees.reduce((sum, e) => {
    if (e.leave == null) return sum;
    const res = Number(e.leave.accruedDays ?? 0) - Number(e.leave.usedDays ?? 0);
    return sum + Math.max(0, res);
  }, 0);

  function getInitials(e: Employee): string {
    const ln = (e.lastName ?? '').trim().charAt(0);
    const fn = (e.firstName ?? '').trim().charAt(0);
    if (ln && fn) return `${ln}${fn}`.toUpperCase();
    if (ln) return ln.toUpperCase();
    if (fn) return fn.toUpperCase();
    return (e.email ?? e.id).charAt(0).toUpperCase();
  }

  return (
    <div className="container-fluid py-4">
      <div className="personnel-hero">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h1>
              <i className="bi bi-people-fill me-2" style={{ opacity: 0.95 }} />
              Area personale
            </h1>
            <p className="personnel-sub mb-0">
              Gestione dipendenti, contratti e ferie
            </p>
          </div>
          <div className="personnel-btn-group">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv,text/csv,text/plain"
              className="d-none"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              className="btn btn-light btn-sm personnel-btn-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Caricamento…
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-1" />
                  Carica file
                </>
              )}
            </button>
            {uploadedFileName && (
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleCancelFile}
                title="Annulla file caricato"
              >
                <i className="bi bi-x-circle me-1" />
                Annulla
              </button>
            )}
            <button
              type="button"
              className="btn btn-light personnel-btn-primary"
              onClick={() => setShowCreate(true)}
            >
              <i className="bi bi-plus-lg me-1" />
              Nuovo dipendente
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-light opacity-75"
              disabled={deletingAll || employees.length === 0}
              onClick={handleDeleteAll}
              title="Elimina l'elenco completo (irreversibile)"
            >
              {deletingAll ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Eliminazione…
                </>
              ) : (
                <>
                  <i className="bi bi-trash3 me-1" />
                  Cancella elenco
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="personnel-kpi-row">
        <div className="personnel-kpi-card">
          <div className="kpi-icon"><i className="bi bi-people" /></div>
          <div className="kpi-value">{employees.length}</div>
          <div className="kpi-label">Dipendenti</div>
        </div>
        <div className="personnel-kpi-card">
          <div className="kpi-icon"><i className="bi bi-calendar-event" /></div>
          <div className="kpi-value">{contractsExpiring}</div>
          <div className="kpi-label">Contratti in scadenza (30gg)</div>
        </div>
        <div className="personnel-kpi-card">
          <div className="kpi-icon"><i className="bi bi-sun" /></div>
          <div className="kpi-value">{totalResidualLeave}</div>
          <div className="kpi-label">Giorni ferie residui</div>
        </div>
      </div>

      {importResult && (
        <div className={`alert alert-${importResult.imported > 0 ? 'success' : 'warning'} py-3 mb-3 rounded-3`}>
          <strong>Importazione:</strong> {importResult.imported} dipendenti importati
          {importResult.errors.length > 0 && (
            <ul className="mb-0 mt-2 small">
              {importResult.errors.slice(0, 5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {importResult.errors.length > 5 && <li>…e altri {importResult.errors.length - 5} errori</li>}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger rounded-3" role="alert">
          {error}
        </div>
      )}

      {loading && !employees.length && (
        <div className="personnel-card p-5 text-center text-muted">
          <div className="spinner-border text-primary mb-2" />
          <div>Caricamento elenco dipendenti…</div>
        </div>
      )}

      {(!loading || employees.length > 0) && (
        <div className="personnel-card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span>
              <i className="bi bi-person-lines-fill me-2" style={{ color: '#0d9488' }} />
              Elenco dipendenti
            </span>
            <span className="personnel-badge">{employees.length}</span>
          </div>
          <div className="card-body p-0">
            {employees.length === 0 && !loading ? (
              <div className="personnel-empty-state">
                <div className="empty-icon"><i className="bi bi-person-plus" /></div>
                <h3>Nessun dipendente</h3>
                <p className="mb-3">Carica un file CSV o aggiungi manualmente un nuovo dipendente.</p>
                <p className="small text-muted mb-0">
                  Formato: Cognome;Nome;Email;Telefono;Codice fiscale;Indirizzo (separatore ; o ,)
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table personnel-table mb-0">
                <thead>
                  <tr>
                    <th>Dipendente</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    <th>Matricola</th>
                    <th>Codice fiscale</th>
                    <th>Livello</th>
                    <th>Qualifica</th>
                    <th className="text-end">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => {
                    const name = e.lastName || e.firstName
                      ? `${e.lastName ?? ''} ${e.firstName ?? ''}`.trim()
                      : e.id;
                    return (
                      <tr key={e.id}>
                        <td>
                          <span className="employee-avatar">{getInitials(e)}</span>
                          <span className="employee-name d-block">{name}</span>
                          {e.matricola && (
                            <span className="text-muted small d-block">
                              Matricola: <code>{e.matricola}</code>
                            </span>
                          )}
                        </td>
                        <td className="text-muted">{e.email || '—'}</td>
                        <td className="text-muted">{e.phone || '—'}</td>
                        <td className="text-muted small">{e.matricola || '—'}</td>
                        <td className="text-muted small">{e.fiscalCode || '—'}</td>
                        <td className="text-muted small">{e.levelCode || '—'}</td>
                        <td className="text-muted small">{e.qualification || '—'}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm personnel-btn-outline me-1"
                            onClick={() => setEditing(e)}
                            title="Modifica"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => setShowDetails(e)}
                            title="Dettaglio dipendente"
                          >
                            <i className="bi bi-card-list" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(e)}
                            title="Elimina"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <EmployeeCreateModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            void load();
            setShowCreate(false);
          }}
        />
      )}
      {editing && (
        <EmployeeEditModal
          show={!!editing}
          employee={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void load();
            setEditing(null);
          }}
        />
      )}
      {showDetails && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: 'rgba(0,0,0,0.35)' }}
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Dettaglio dipendente – {showDetails.lastName} {showDetails.firstName}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDetails(null)} />
              </div>
              <div className="modal-body">
                <EmployeeDetails employee={showDetails} />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowDetails(null)}
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
